import { ApprovalState } from '../models/approval-state';
import { AccountsPayableAction } from '../models/accounts-payable-actions.model';
import { UserRoleModel } from '../models/user-role-model';
import { RoleConstant } from '../models/role-constant';

export const BYPASS_AP_MANAGER1_CONFIG_KEY = 'ByPassApManager1';


/**
 * "Bypass AP Manager 1" for the company, from `userRoles[].company.additionalConfigurations` in /me
 * (set by the System Admin in Product Configurations). Off when the company doesn't send it.
 */
export function getBypassApManager1FromUserRoles(
  userRoles: UserRoleModel[] | null | undefined,
  companyId: string | null | undefined
): boolean {
  if (!companyId) {
    return false;
  }

  const company = (userRoles ?? []).find((role) => role.company?.id === companyId)?.company;
  const entry = company?.additionalConfigurations?.find(
    (config) => config.key === BYPASS_AP_MANAGER1_CONFIG_KEY
  );

  return entry?.isEnabled === true;
}

export function isApManager1ApprovalSkipped(
  approvals: Array<{ role?: { id?: string }; state?: string }> | null | undefined
): boolean {
  return Boolean(
    (approvals || []).find(
      (approval) =>
        approval?.role?.id === RoleConstant.accountsPayableManager1 &&
        approval?.state === ApprovalState.skipped
    )
  );
}

export function isActiveApManager1Role(
  roleIds: string[],
  bypassApManager1: boolean
): boolean {
  return (
    roleIds.includes(RoleConstant.accountsPayableManager1) && !bypassApManager1
  );
}

export function canApManagerEditAccountsPayable(
  roleIds: string[],
  bypassApManager1: boolean
): boolean {
  return (
    roleIds.includes(RoleConstant.accountsPayableManager2) ||
    isActiveApManager1Role(roleIds, bypassApManager1)
  );
}

export function isAccountsPayableManager2Role(roleIds: string[]): boolean {
  return roleIds.includes(RoleConstant.accountsPayableManager2);
}

export function isAccountsPayableCreatorRole(roleIds: string[]): boolean {
  return roleIds.includes(RoleConstant.accountsPayableCreator);
}

export function canUserAccessCollatedPayment(
  roleId: string | null | undefined,
  bypassApManager1: boolean
): boolean {
  if (!roleId) {
    return false;
  }

  if (roleId === RoleConstant.companyAdminId) {
    return true;
  }

  if (roleId === RoleConstant.accountsPayableManager2) {
    return true;
  }

  if (roleId === RoleConstant.accountsPayableManager1) {
    return !bypassApManager1;
  }

  return false;
}

export function canApManager1MakePayment(
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean,
  approvals: Array<{ role?: { id?: string }; state?: string }> | null | undefined
): boolean {
  if (selectedRoleId !== RoleConstant.accountsPayableManager1) {
    return true;
  }

  if (bypassApManager1 || isApManager1ApprovalSkipped(approvals)) {
    return false;
  }

  return true;
}

/** When ByPassApManager1 is on, AP Manager 1 is skipped — no approve/pay actions. */
const BYPASSED_AP_MANAGER1_BLOCKED_ACTIONS = new Set<string>([
  AccountsPayableAction.Approve,
  AccountsPayableAction.Reject,
  AccountsPayableAction.Return,
  AccountsPayableAction.MakePayment,
  AccountsPayableAction.EditPayment,
  AccountsPayableAction.CollatedPayment,
  AccountsPayableAction.IntegrateToNetSuite,
]);

export function isBypassedApManager1ActionBlocked(
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean,
  action: string
): boolean {
  if (
    !bypassApManager1 ||
    selectedRoleId !== RoleConstant.accountsPayableManager1
  ) {
    return false;
  }
  return BYPASSED_AP_MANAGER1_BLOCKED_ACTIONS.has(action);
}

type ApproverIdentityRef = {
  id?: string;
  userId?: string;
  applicationUserId?: string;
};

type AccountsPayableApprovalRow = {
  role?: { id?: string };
  state?: string;
  approver?: ApproverIdentityRef;
};

function getUserIdentityIds(user: any): string[] {
  return [
    user?.id,
    user?.userId,
    user?.sub,
    user?.user?.id,
    user?.user?.userId,
  ]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim().toLowerCase());
}

function getApproverIdentityIds(approver?: ApproverIdentityRef): string[] {
  return [approver?.id, approver?.userId, approver?.applicationUserId]
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim().toLowerCase());
}

/** User must be listed on the approval row for their currently selected role. */
export function isCurrentUserListedAsApproverForRole(
  approvals: AccountsPayableApprovalRow[] | null | undefined,
  selectedRoleId: string | null | undefined,
  user: any
): boolean {
  if (!selectedRoleId || !user) {
    return false;
  }

  const currentUserIds = getUserIdentityIds(user);
  if (!currentUserIds.length) {
    return false;
  }

  return (approvals || []).some((approval) => {
    if (approval?.role?.id !== selectedRoleId) {
      return false;
    }

    const approverIds = getApproverIdentityIds(approval?.approver);
    return approverIds.some((id) => currentUserIds.includes(id));
  });
}

/** AP Manager 1/2 and Company Admin may make payments when listed on the approval row. */
export function canUserMakeAccountsPayablePayment(
  selectedRoleId: string | null | undefined,
  bypassApManager1: boolean,
  approvals: AccountsPayableApprovalRow[] | null | undefined,
  user: any
): boolean {
  if (!selectedRoleId || selectedRoleId === RoleConstant.accountsPayableCreator) {
    return false;
  }

  if (!canApManager1MakePayment(selectedRoleId, bypassApManager1, approvals)) {
    return false;
  }

  const paymentCapableRoles: readonly string[] = [
    RoleConstant.accountsPayableManager1,
    RoleConstant.accountsPayableManager2,
    RoleConstant.companyAdminId,
  ];

  if (!paymentCapableRoles.includes(selectedRoleId)) {
    return false;
  }

  return isCurrentUserListedAsApproverForRole(approvals, selectedRoleId, user);
}
